/**
 * User-tag filter - manual tags added via the sidebar editor
 * (e.g. "exam-prep", "interview-prep"). Thin wrapper around
 * `TagChipPicker` with no color dot (user tags don't carry a domain
 * palette) and the user-tag specific empty copy.
 */

import { useMemo, useState } from 'react';
import { Bookmark } from 'lucide-react';
import { useUserTagOptions } from '@/hooks/mindspace/useMindspaceOptions';
import { useTagFilters } from '@/hooks/mindspace/useTagFilters';
import type { KnowledgeGraphNode } from '@/lib/api-client';
import { FilterSection } from '@/components/features/mindspace/filters/FilterSection';
import {
  TagChipPicker,
  toggleTagInList,
} from '@/components/features/mindspace/filters/TagChipPicker';

interface UserTagFilterProps {
  /** Current filtered graph nodes - only used for the dim-when-zero hint. */
  nodes: KnowledgeGraphNode[] | undefined;
  /** Section open on first render. Story-only hatch, initial state only — no
   *  production call site passes it, and the panels all mount collapsed. It
   *  exists because the section body (chips, window pills, the lesson list) is
   *  otherwise reachable only by clicking, and a click-only state has no resting
   *  story to diff. Same escape hatch as `FlowSelector`/`SearchInput`'s
   *  `defaultOpen`. */
  defaultOpen?: boolean;
}

export function UserTagFilter({
  nodes,
  defaultOpen = false,
}: UserTagFilterProps) {
  const { selectedUser, setFilters } = useTagFilters();
  const [open, setOpen] = useState(defaultOpen);

  const { data: options = [], isLoading } = useUserTagOptions();

  const availableInView = useMemo(() => {
    const s = new Set<string>();
    for (const n of nodes ?? []) {
      for (const t of n.user_tags ?? []) s.add(t);
    }
    return s;
  }, [nodes]);

  return (
    <FilterSection
      icon={<Bookmark className='h-3.5 w-3.5' />}
      label='Your tags'
      count={selectedUser.length}
      open={open}
      onOpenChange={setOpen}
      onClear={selectedUser.length ? () => setFilters({ user: [] }) : undefined}
    >
      <TagChipPicker
        options={options}
        selected={selectedUser}
        onToggle={tag =>
          setFilters({ user: toggleTagInList(selectedUser, tag) })
        }
        availableInView={availableInView}
        isLoading={isLoading}
        emptyLabel='No user tags yet - add some from the concept sidebar'
        searchPlaceholder='Filter your tags…'
      />
    </FilterSection>
  );
}
